import React, { useState } from 'react';
import { Search, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import type { AuditReportData } from './types';
import AuditReport from './components/AuditReport';

export default function App() {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [report, setReport] = useState<AuditReportData | null>(null);
  const [error, setError] = useState<string>('');
  const [forceRefresh, setForceRefresh] = useState<boolean>(false);

  // PROD: This uses Vite's built-in env handler
  const API_URL = import.meta.env.VITE_API_URL || "";

  // Safer domain validation without vulnerable RegExp
  const isValidDomain = (input: string) => {
    try {
      // Prepend http if missing to ensure proper URL parsing
      const urlString = input.startsWith('http') ? input : `https://${input}`;
      const url = new URL(urlString);
      // Ensure the hostname has at least one dot (and doesn't end with one) or is localhost
      return (url.hostname.includes('.') && !url.hostname.endsWith('.')) || url.hostname === 'localhost';
    } catch {
      return false;
    }
  };

  const handleAudit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    // Form Validation
    if (!isValidDomain(url)) {
      setError("Please enter a valid domain (e.g., example.com)");
      return;
    }

    setLoading(true);
    setError('');
    setReport(null);

    const sanitizedUrl = url.startsWith('http') ? url : `https://${url}`;

    try {
      // Append query param if force refresh is checked
      const endpoint = forceRefresh
        ? `${API_URL}/api/audit?refresh=true`
        : `${API_URL}/api/audit`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: sanitizedUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'The forensic scan failed.');
      }

      const data: AuditReportData = await response.json();
      if (data.error) setError(data.error);
      else setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans p-6">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <header className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-indigo-600 rounded-2xl text-white mb-6 shadow-lg shadow-indigo-200">
            <ShieldCheck size={32} />
          </div>
          <h1 className="text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">GEO Forensic Auditor</h1>
          <p className="text-slate-500 text-lg max-w-lg mx-auto">Validate your brand's Entity Graph and machine-readability for Generative AI engines.</p>
        </header>

        <section className="mb-10">
          <form onSubmit={handleAudit} className="group relative bg-white p-2 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200 flex items-center focus-within:ring-4 ring-indigo-500/10 transition-all">
            <div className="pl-4 text-slate-400 group-focus-within:text-indigo-500"><Search size={22} /></div>
            <input type="text" placeholder="Enter domain (e.g., keilanimedia.com)" value={url} onChange={(e) => setUrl(e.target.value)} disabled={loading} className="flex-1 p-4 bg-transparent outline-none text-lg" />
            <button type="submit" disabled={loading || !url} className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-bold py-3 px-8 rounded-xl transition-all flex items-center gap-2">
              {loading ? <Loader2 className="animate-spin" size={20} /> : null}
              {loading ? 'Scanning...' : 'Audit Entity'}
            </button>
          </form>
          <div className="flex items-center justify-end mt-2 pr-2">
            <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer hover:text-indigo-600 transition-colors">
              <input
                type="checkbox"
                checked={forceRefresh}
                onChange={(e) => setForceRefresh(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Fresh Scan (Clear Cache)
            </label>
          </div>
        </section>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-5 rounded-2xl flex items-start gap-3 mb-8 animate-in zoom-in-95">
            <AlertCircle className="mt-0.5 shrink-0" size={20} />
            <div><h3 className="font-bold mb-1">Audit Blocked</h3><p className="text-sm opacity-90">{error}</p></div>
          </div>
        )}

        <main>
          {loading && !report && (
            <div className="py-20 text-center animate-pulse">
              <div className="inline-block p-4 bg-indigo-50 rounded-full text-indigo-600 mb-4"><Loader2 className="animate-spin" size={32} /></div>
              <p className="text-slate-400 font-medium">Rendering machine-view and parsing Entity Graph...</p>
            </div>
          )}
          {report && !loading && <AuditReport data={report} />}
        </main>
      </div>
    </div>
  );
}